/**
 * tutorEarningsAdapter — le masteur connecté (`UserResource`) → « Mon mois » (`UITutorEarnings`).
 *
 * Port littéral de l'ancienne « Mon historique », `CoachStats.js`. Elle ne lisait rien d'autre que
 * le compte, `state.data.currentUser.object`, rempli par `authService.getUser` (`GET api/user`) :
 *  - `statistics.lives.this_month` / `.last_month` — `TutorTrait::getTutorStatistics()` →
 *    `getLiveStatisticsBetween()`, des sommes de `live_occurrences.live_units` (heures) et
 *    `transfer_amount` (euros), flottants en base ;
 *  - `stripe_connect_details_submitted` — le compte Stripe Express rempli (`UserResource`, lu chez
 *    Stripe à chaque `getUser`).
 * Aucun appel à part : le compte rechargé recharge le mois.
 *
 * Les deux branches de l'ancienne carte :
 *  - compte Stripe rempli : `pending_transfer_*` (« en attente de validation ») et `transferred_*`
 *    (« validé ») — l'ancienne appelait « validé » ce qui est VIRÉ, et « en attente » ce qui ne l'est pas
 *    encore, relu ou non ;
 *  - sans : `validated_live_units` et `validated_transfer_amount` (« montant validé cumulé »), et
 *    l'astérisque quand `pending_review_live_units > 0`.
 */
import moment from "moment"

import { formatDateRange } from "../../../ui/formatters/dateFormatter"
import { type UITutorEarnings, type UITutorEarningsPeriodKey } from "../../../ui/types/tutorCoquilleAccueil"

// ── Formes lâches : le compte vient d'un réducteur en JS, tout est optionnel ──────────

/** Une somme SQL : un nombre, parfois une chaîne selon le pilote. */
type ApiAmount = number | string | null

export type ApiTutorLiveStatistics = {
    pending_review_live_units?: ApiAmount
    validated_live_units?: ApiAmount
    validated_transfer_amount?: ApiAmount
    pending_transfer_live_units?: ApiAmount
    transferred_live_units?: ApiAmount
    pending_transfer_amount?: ApiAmount
    transferred_transfer_amount?: ApiAmount
}

export type ApiTutorEarningsUser = {
    roles?: string[] | null
    stripe_connect_details_submitted?: boolean | null
    statistics?: {
        lives?: Partial<Record<UITutorEarningsPeriodKey, ApiTutorLiveStatistics | null>> | null
    } | null
}

/**
 * Divergence voulue : l'ancienne branche sans Stripe écrivait le champ tel quel (« undefined€ » sur un
 * compte sans statistiques) ; la branche Stripe le ramenait déjà à 0. Les deux le font ici.
 */
const toAmount = (value: ApiAmount | undefined): number => {
    if (value == null || value === "") return 0
    const n = typeof value === "number" ? value : Number(value)
    return Number.isFinite(n) ? n : 0
}

/**
 * Les dates de « Mon mois » : ce mois-ci court du 1er à AUJOURD'HUI inclus, la période que le serveur
 * somme (`getLiveStatisticsBetween`, jusqu'à maintenant) et celle que « Mon score » écrit à côté.
 *
 * Divergence voulue avec l'ancienne carte, tranchée le 05/11 : elle s'arrêtait la veille passé minuit
 * de 25 minutes (`moment().add(-25, "minutes").add(-1, "days")`), donc un jour avant ce que les
 * chiffres comptaient, et le 1er du mois (le 2 avant 0 h 25) la fin tombait avant le début (« Du 01
 * nov. au 31 oct. »). Le 1er, la ligne dit maintenant « Le 1er novembre ».
 */
function rangeLabel(period: UITutorEarningsPeriodKey): string {
    if (period === "this_month") {
        const now = moment()
        return formatDateRange(now.clone().startOf("month").toISOString(), now.toISOString())
    }
    const lastMonth = moment().add(-1, "month")
    return formatDateRange(
        lastMonth.clone().startOf("month").toISOString(),
        lastMonth.clone().endOf("month").toISOString()
    )
}

/**
 * « Mon mois ». `null` quand il n'y a pas de compte, ou que ce compte n'est pas tuteur — l'ancienne
 * carte ne rendait rien hors de `hasRole(user, "tutor")`.
 */
export function toUITutorEarnings(user: ApiTutorEarningsUser | null | undefined): UITutorEarnings | null {
    if (user == null || !(user.roles ?? []).includes("tutor")) return null
    const stripeConnected = user.stripe_connect_details_submitted === true

    const toPeriod = (key: UITutorEarningsPeriodKey): UITutorEarnings["periods"][UITutorEarningsPeriodKey] => {
        const lives = user.statistics?.lives?.[key]
        const pendingReview = toAmount(lives?.pending_review_live_units) > 0
        if (stripeConnected) {
            return {
                rangeLabel: rangeLabel(key),
                validatedHours: toAmount(lives?.transferred_live_units),
                pendingHours: toAmount(lives?.pending_transfer_live_units),
                validatedAmount: toAmount(lives?.transferred_transfer_amount),
                pendingAmount: toAmount(lives?.pending_transfer_amount),
                // L'ancienne posait l'astérisque sur les dates dans les deux branches, sans sa note avec
                // Stripe ; la carte ne la montre plus qu'avec la note, mais le fait reste dit.
                pendingReview,
            }
        }
        return {
            rangeLabel: rangeLabel(key),
            validatedHours: toAmount(lives?.validated_live_units),
            validatedAmount: toAmount(lives?.validated_transfer_amount),
            pendingReview,
        }
    }

    return {
        stripeConnected,
        periods: { this_month: toPeriod("this_month"), last_month: toPeriod("last_month") },
    }
}
